/**
 * Dialog for restoring a PostgreSQL database using pg_restore or psql.
 * Supports .dump (custom format) and .sql (plain SQL) files.
 */

import { useState, useEffect } from 'react';
import { AlertTriangle, Terminal, Upload, Settings } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Input } from '../../../components/ui/input';
import { Checkbox } from '../../../components/ui/checkbox';
import { ProgressIndicator } from './ProgressIndicator';
import { usePostgresRestore, usePgRestoreAvailability } from '../hooks/usePostgresRestore';
import { useOperationStatus } from '../hooks/useTaskProgress';
import { useBackupRestoreStore } from '../store/backupRestoreStore';
import { useAppStore } from '../../../store/appStore';
import { toast } from '../../../components/ui/toaster';
import type { PgsqlRestoreFormat } from '../types/backupRestore';
import { ExternalToolHowToFixDialog } from '../../shared/external-tools/components/ExternalToolHowToFixDialog';

type PostgresRestoreDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  connectionId: string;
  databaseName: string;
};

export function PostgresRestoreDialog({
  open,
  onOpenChange,
  connectionId,
  databaseName,
}: PostgresRestoreDialogProps) {
  const [targetDatabase, setTargetDatabase] = useState(databaseName);
  const [createDatabase, setCreateDatabase] = useState(false);
  const [dropExisting, setDropExisting] = useState(false);
  const [operationId, setOperationId] = useState<string | null>(null);
  const [showHowToFix, setShowHowToFix] = useState(false);

  const operation = useOperationStatus(operationId);
  const { removeOperation } = useBackupRestoreStore();
  const pgsqlToolsSettings = useAppStore((state) => state.pgsqlToolsSettings);
  const customBinPath = pgsqlToolsSettings.customBinPath?.trim() || undefined;
  const { isAvailable, error: toolError, isChecking } = usePgRestoreAvailability(customBinPath);

  const { restoreDatabase, isRestoring, reset, detectedFormat } = usePostgresRestore({
    onSuccess: (result, opId) => {
      setOperationId(opId);

      if (result.success) {
        toast.success({
          title: 'Restore Complete',
          description: `Database "${targetDatabase}" restored successfully`,
        });
      } else if (!result.cancelled) {
        toast.error({
          title: 'Restore Failed',
          description: result.error || 'Unknown error occurred',
        });
      }
    },
    onError: (error) => {
      toast.error({
        title: 'Restore Failed',
        description: error.message,
      });
    },
  });

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setTargetDatabase(databaseName);
      setCreateDatabase(false);
      setDropExisting(false);
      setOperationId(null);
      reset();
    }
  }, [open, databaseName, reset]);

	  const handleStartRestore = () => {
	    restoreDatabase({
	      connectionId,
	      targetDatabaseName: targetDatabase,
	      filePath: '', // Will trigger open dialog
	      format: 'custom' as PgsqlRestoreFormat, // Will be auto-detected
	      customBinPath,
	      createDatabase,
	      dropExisting,
	    });
	  };

  const handleClose = () => {
    if (operationId) {
      removeOperation(operationId);
    }
    setOperationId(null);
    reset();
    onOpenChange(false);
  };

  const isComplete =
    operation?.status === 'succeeded' ||
    operation?.status === 'failed' ||
    operation?.status === 'cancelled';
  const canClose = isComplete || (!isRestoring && !operationId);
  const hasStarted = isRestoring || operationId !== null;

  return (
    <>
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && canClose && handleClose()}>
        <DialogContent data-testid="postgres-restore-dialog">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
                <Upload className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <DialogTitle>Restore Database</DialogTitle>
                <DialogDescription className="mt-1">
                  Restore to &quot;{targetDatabase}&quot; using pg_restore or psql
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="py-4 space-y-4">
            {/* Tool availability warning */}
            {!isChecking && !isAvailable && (
              <div className="flex items-start gap-3 p-3 rounded-md bg-red-50 border border-red-200 dark:bg-red-950 dark:border-red-800">
                <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
                <div className="space-y-2 text-sm text-red-800 dark:text-red-200">
                  <div className="flex items-start gap-2">
                    <Terminal className="h-4 w-4 shrink-0 mt-0.5" />
                    <span>
                      {toolError || 'pg_restore/psql not found. Please install PostgreSQL client tools.'}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    <div className="flex items-center gap-2">
                      <Settings className="h-3 w-3 shrink-0" />
                      <span>Configure the tools path in Settings → Advanced → PostgreSQL Tools</span>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 px-2 text-xs"
                      onClick={() => setShowHowToFix(true)}
                      data-testid="postgres-restore-how-to-fix"
                    >
                      How to fix
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Destructive action warning */}
            <div
              className="flex items-start gap-3 p-3 rounded-md bg-red-50 border border-red-200 dark:bg-red-950 dark:border-red-800"
              data-testid="destructive-warning"
            >
              <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
              <div className="text-sm text-red-800 dark:text-red-200">
                <strong>Warning:</strong> This operation may overwrite existing data in the target
                database. Make sure you have a backup before proceeding.
              </div>
            </div>

            {!hasStarted ? (
              <>
                {/* Target database name */}
                <div className="space-y-2">
                  <Label htmlFor="target-database">Target Database</Label>
                  <Input
                    id="target-database"
                    value={targetDatabase}
                    onChange={(e) => setTargetDatabase(e.target.value)}
                    placeholder="Enter target database name"
                    disabled={!isAvailable}
                    data-testid="target-database-input"
                  />
                </div>

                {/* Options */}
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="create-database"
                      checked={createDatabase}
                      onCheckedChange={(checked) => setCreateDatabase(checked === true)}
                      disabled={!isAvailable}
                    />
                    <Label htmlFor="create-database" className="text-sm font-normal">
                      Create database if it doesn&apos;t exist
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="drop-existing"
                      checked={dropExisting}
                      onCheckedChange={(checked) => setDropExisting(checked === true)}
                      disabled={!isAvailable}
                    />
                    <Label htmlFor="drop-existing" className="text-sm font-normal">
                      Drop existing objects before restore
                    </Label>
                  </div>
                </div>

                {/* Info about file selection */}
                <div className="text-sm text-muted-foreground">
                  You will be prompted to select a backup file (.dump or .sql) from your computer.
                  The format will be auto-detected.
                </div>
              </>
            ) : (
              /* Progress indicator */
              <div className="space-y-2">
                {detectedFormat && (
                  <div className="text-sm text-muted-foreground">
                    Detected format: {detectedFormat === 'custom' ? 'Custom (.dump)' : 'Plain SQL (.sql)'}
                  </div>
                )}
                <ProgressIndicator
                  status={operation?.status || 'inProgress'}
                  progress={operation?.progress}
                  message={operation?.message || 'Running restore...'}
                  error={operation?.error}
                  data-testid="restore-progress"
                />
              </div>
            )}
          </div>

          <DialogFooter>
            {!hasStarted ? (
              <>
                <Button
                  variant="outline"
                  onClick={handleClose}
                  data-testid="restore-dialog-cancel"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleStartRestore}
                  disabled={!isAvailable || isChecking || !targetDatabase.trim()}
                  data-testid="restore-dialog-start"
                >
                  Select File & Restore
                </Button>
              </>
            ) : (
              <Button
                variant={isComplete ? 'default' : 'outline'}
                onClick={handleClose}
                disabled={!canClose}
                data-testid="restore-dialog-close"
              >
                {isComplete ? 'Done' : 'Cancel'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ExternalToolHowToFixDialog
        open={showHowToFix}
        onOpenChange={setShowHowToFix}
        topic="postgres"
        missingTools={['pg_restore', 'psql']}
      />
    </>
  );
}
